import * as React from "react";

import {
  ISwatchColorPickerProps,
  ISwatchColorPickerStyleProps,
  ISwatchColorPickerStyles,
  SwatchColorPickerBase,
} from "@fluentui/react/lib/SwatchColorPicker";
import { elementContains, styled } from "@fluentui/react/lib/Utilities";

import { isIdleCallbackEnabled } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import {
  computeNextShadowRenderingIncrement,
  withShadowRendering,
} from "@1js/acui-shadow-rendering";
import { AppTooltipHost } from "@1js/acui-tooltip";

import { AppColorPickerProps as AppColorPickerExternalProps } from "./AppColorPicker.Props";
import { getStyles } from "./AppColorPicker.styles";
import { getColorGridCellStyles } from "./AppColorPickerGridCell.styles";

type AppColorPickerProps = AppColorPickerExternalProps & {
  isShadowRendering?: boolean;
};

const AppSwatchColorPicker = styled<
  ISwatchColorPickerProps,
  ISwatchColorPickerStyleProps,
  ISwatchColorPickerStyles
>(
  SwatchColorPickerBase,
  getStyles,
  undefined /* getProps */,
  undefined /* customizable */,
  true /* pure */
);

// The query selector used to target individual color cells in the DOM
const colorPickerQuerySelector = (id: string): string => {
  return '[id*="-' + id + '-"]';
};

export interface AppColorPickerState {
  itemsToRender?: number;
}

/**
 * To improve perf., this component can potentially be in shadow rendering phase - in that phase it will incrementally render
 * more of its cells behind the scenes in browser idle time. We preserve these incrementally rendered cells
 * in the DOM, so that when the user opens this component in a menu, it opens fast.
 *
 * To make this work, please use the helper function `getColorCellsToRender` whenever you have to work with the colorCells
 * prop.
 */
export class AppColorPickerBase extends React.PureComponent<
  AppColorPickerProps,
  AppColorPickerState
> {
  // Keeping a reference to the AppTooltipHost allows us to unregister the action listeners once the component is unmounted
  private tooltipHostMap: Map<string, AppTooltipHost> = new Map();

  // By using the div wrapper as the base, we can search for the internal color picker grid cells without the risk of finding the wrong element in the DOM
  private colorPickerWrapper = React.createRef<HTMLDivElement>();

  private currentShadowRenderingStart: number | undefined;
  private totalShadowRenderingTime: number = 0;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(props: AppColorPickerProps) {
    super(props);
    this.state = {};
  }

  public render() {
    return (
      <div
        ref={this.colorPickerWrapper}
        onBlur={this.props.blurOnInteralEvents ? undefined : this.onPickerBlur}
      >
        <AppSwatchColorPicker
          disabled={this.props.disabled}
          selectedId={this.props.selectedId}
          cellShape={"square"}
          styles={this.props.styles}
          getColorGridCellStyles={getColorGridCellStyles(this.props.cellStyles)}
          columnCount={this.props.columnCount}
          colorCells={this.getColorCellsToRender()}
          onColorChanged={this.onExecute}
          onCellFocused={this.onPreviewExecute}
          doNotContainWithinFocusZone={true}
          aria-label={this.props.ariaLabel}
          ariaPosInSet={this.props.positionInSet}
          ariaSetSize={this.props.setSize}
          data-no-vertical-wrap={true}
          data-no-horizontal-wrap={false}
          focusOnHover={true}
          mouseLeaveParentSelector={'[tabindex="0"][role="menu"]'}
          isControlled={true}
        />
      </div>
    );
  }

  public static getDerivedStateFromProps(
    newProps: AppColorPickerProps,
    state: AppColorPickerState
  ) {
    if (newProps.isShadowRendering && state.itemsToRender === undefined) {
      return {
        itemsToRender: 0,
      };
    }

    return null;
  }

  public componentDidMount() {
    this.applyTooltipsToGridCells();
    this.removeBrowserTooltipFromCells();
    this.renderMoreItemsInIdleCallback();
  }

  public componentDidUpdate() {
    this.applyTooltipsToGridCells();
    this.removeBrowserTooltipFromCells();
    this.renderMoreItemsInIdleCallback();
  }

  public componentWillUnmount() {
    this.tooltipHostMap.forEach((tooltipHost) => {
      tooltipHost.unregisterTooltipHostActions();
    });
  }

  /**
   * This method increments the number of items to render in shadow mode. And these new items are mounted
   * in an idle callback.
   */
  private renderMoreItemsInIdleCallback = () => {
    if (
      this.props.isShadowRendering &&
      this.state.itemsToRender !== undefined &&
      this.state.itemsToRender < this.props.colorCells.length
    ) {
      if (this.currentShadowRenderingStart) {
        this.totalShadowRenderingTime +=
          performance.now() - this.currentShadowRenderingStart;
      }

      const nextIncrement = computeNextShadowRenderingIncrement(
        this.totalShadowRenderingTime,
        this.state.itemsToRender
      );

      isIdleCallbackEnabled() &&
        // This state update is little tricky. Normally, for counters, we need to use the functional version
        // to not miss state updates. But here, we prefer to use this because we do not care if we miss
        // a few updates, but we do not want two state updates to combined in one render.
        window.requestIdleCallback(
          () => {
            this.currentShadowRenderingStart = performance.now();
            this.setState({
              itemsToRender: this.state.itemsToRender! + nextIncrement,
            });
          },
          undefined /* options*/,
          `ColorPicker-${this.props.id}-ShadowRenderIncrement`
        );
    }
  };

  /**
   * In shadow rendering mode, we might only want to render some of the color cells
   * This method takes care of that.
   */
  private getColorCellsToRender = () => {
    return this.props.isShadowRendering &&
      this.state.itemsToRender !== undefined &&
      this.state.itemsToRender < this.props.colorCells.length
      ? this.props.colorCells.slice(0, this.state.itemsToRender)
      : this.props.colorCells;
  };

  private onExecute = (colorId: string, color: string) => {
    if (this.props.onExecute) {
      const { id, location, shouldTakeFocus } = this.props;
      this.props.onExecute({
        id,
        selectedColorId: colorId,
        color,
        location,
        shouldTakeFocus,
      });
    }
  };

  private onPreviewExecute = (colorId: string, color: string) => {
    // Check if colorId and color are not undefined.
    // If they are undefined, a color lost focus
    if (this.props.onFocus && colorId && color) {
      this.props.onFocus({
        id: this.props.id,
        selectedColorId: colorId,
        color,
      });
    } else if (
      this.props.onBlur &&
      !colorId &&
      !color &&
      this.props.blurOnInteralEvents
    ) {
      this.props.onBlur();
    }
  };

  private onPickerBlur = (ev: React.FocusEvent<HTMLDivElement>) => {
    if (
      this.props.onBlur &&
      !elementContains(
        this.colorPickerWrapper.current,
        ev.relatedTarget as HTMLElement
      )
    ) {
      this.props.onBlur();
    }
  };

  /**
   * Color picker grid cells are defined in fabric, so it's a bit more difficult to apply our tooltips to them.
   * It can be done by registering a Tooltiphost on each cell by iterating through the colorCells prop and
   * finding each button element.
   */
  private applyTooltipsToGridCells = () => {
    if (
      checkExperiment(
        this.context,
        ribbonExperiments.disableColorPickerFancyTooltips
      )
    ) {
      return;
    }

    this.getColorCellsToRender().map((cell) => {
      if (this.context.updateTooltipsHidden && this.context.setTooltipElement) {
        const tooltipHost = new AppTooltipHost({
          updateTooltipsHidden: this.context.updateTooltipsHidden,
          setTooltipElement: this.context.setTooltipElement,
          htmlElement:
            this.colorPickerWrapper.current &&
            this.colorPickerWrapper.current.querySelector<HTMLElement>(
              colorPickerQuerySelector(cell.id)
            ),
          controlId: cell.id,
          controlLabel: cell.label || "",
          tooltipTopMargin: 0,
          isMenuComponent: true,
          tooltipDirection: this.props.tooltipDirection,
          performSetOnNextFrame: !!checkExperiment(
            this.context,
            ribbonExperiments.delayRenderTooltipsIsEnabled
          ),
        });
        tooltipHost.registerTooltipHostActions();
        this.tooltipHostMap.set(cell.id, tooltipHost);
      }
    });
  };

  /**
   * We avoid rendering the browser tooltip by stripping the title attribute. The title is set from each IColorCellProp's label,
   * but this value is also used by fabric to set the aria-label, so that value can't be altered.
   */
  private removeBrowserTooltipFromCells = () => {
    if (
      checkExperiment(
        this.context,
        ribbonExperiments.disableColorPickerFancyTooltips
      )
    ) {
      return;
    }

    if (this.context.updateTooltipsHidden && this.context.setTooltipElement) {
      this.getColorCellsToRender().map((cell) => {
        const colorCellButtonElement =
          this.colorPickerWrapper.current &&
          this.colorPickerWrapper.current.querySelector(
            colorPickerQuerySelector(cell.id)
          );
        if (colorCellButtonElement) {
          colorCellButtonElement.setAttribute("title", "");
        }
      });
    }
  };
}

/**
 * Injecting the shadow rendering context props.
 * Externally, you do not need to care about this at all. Shadow rendering is just
 * an implementation detail of this component.
 */
export const AppColorPicker =
  withShadowRendering<AppColorPickerProps>(AppColorPickerBase);
